import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  type AccountInfo,
  type IPublicClientApplication,
} from '@azure/msal-browser'
import logger from '../../app/middleware/log'
import { loginRequest } from '../../authConfig'
import { toSafeInternalPath } from './authRedirect'

// The MSAL `state` to carry through a mid-session re-auth redirect: the user's CURRENT in-app location
// (origin-validated to a relative path), NOT loginRequest.state — that is set once at boot in
// configureAuth() and is never refreshed on client-side SPA navigation, so spreading ...loginRequest
// alone would return the user to their boot page rather than where they are now. undefined when the
// location doesn't resolve to this origin; AuthCallbackPage then falls back to the dashboard.
export const currentReturnPath = (): string | undefined =>
  toSafeInternalPath(window.location.pathname + window.location.search + window.location.hash) ?? undefined

// Silent-renewal failures that only an interactive redirect can recover from. When the SPA refresh token
// is dead (Entra caps it at 24h for browser clients — guaranteed after a weekend-idle tab), MSAL falls
// back to a hidden iframe against ciamlogin.com. If the IdP answers, the failure comes back as an
// InteractionRequiredAuthError. But when third-party cookie blocking leaves the iframe session-less, the
// iframe never broadcasts a response and acquireTokenSilent rejects with BrowserAuthError `timed_out`
// instead. The old classification treated that as "not an auth problem", so the request went out
// tokenless and the user got a raw 401 with no way forward.
const isSilentRenewalDeadEnd = (error: unknown): boolean =>
  error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.timedOut

export const isInteractionRequiredError = (error: unknown): boolean =>
  error instanceof InteractionRequiredAuthError || isSilentRenewalDeadEnd(error)

// The redirect currently navigating away, shared by every caller it halted. One user action typically
// fires several API calls at once; when the token is dead they all fail silent acquisition together, and
// each would start its own redirect — MSAL throws interaction_in_progress on the second. One launch, and
// one promise handed to all of them, so a FAILED launch rejects every halted request (a per-caller
// promise would leave the deduped ones hanging forever on a page that never navigated).
let redirectLaunch: Promise<never> | null = null

// TEST-ONLY: clears the shared launch, which would otherwise leak between jest tests.
export const resetInteractiveReauthForTests = (): void => {
  redirectLaunch = null
}

// Starts the interactive redirect (deduped) and returns a promise that never resolves: the page is about
// to unload into the redirect, so settling the halted request would only race error UI against the
// navigation — users saw exactly that 401 flash mid-redirect. It rejects (for every halted caller) only
// if the redirect itself fails to launch, i.e. only when we are NOT navigating away after all.
export const haltForInteractiveReauth = (
  instance: IPublicClientApplication,
  account: AccountInfo,
  extras: { claims?: string } = {},
): Promise<never> => {
  if (redirectLaunch === null) {
    redirectLaunch = new Promise<never>((_resolve, reject) => {
      instance
        .acquireTokenRedirect({ ...loginRequest, account, ...extras, state: currentReturnPath() })
        .catch((error: unknown) => {
          redirectLaunch = null
          logger.error(error)
          reject(error)
        })
    })
  }
  return redirectLaunch
}
